// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import type { CollabManager } from '@databases/utils/redux/collab/collab-manager';

/**
 * This class is meant to manage the entire lifecycle of collab managers.
 * Currently, it stores a mapping of content IDs to collab managers.
 * In the future, we want this class to handle the creation of collab managers as well.

 */
type AttachListener = (collabManager: CollabManager) => void;

export class CollabStateManager {
  #collabManagerMap: Map<string, CollabManager> = new Map();
  #attachListeners: Map<string, Set<AttachListener>> = new Map();

  setCollabManager(contentId: string, collabManager: CollabManager): void {
    this.#collabManagerMap.set(contentId, collabManager);

    const listeners = this.#attachListeners.get(contentId);

    if (listeners) {
      // Snapshot so listener mutation during iteration is safe.
      [...listeners].forEach((listener) => listener(collabManager));
    }
  }

  getCollabManager(contentId: string): CollabManager | undefined {
    return this.#collabManagerMap.get(contentId);
  }

  /**
   * Subscribe to "a collab manager became available for this contentId".
   * If a manager is ALREADY present, the listener is invoked synchronously
   * with the current manager — callers do not need to special-case the
   * already-attached path. Returns an unsubscribe function.
   */
  onCollabManagerAttached(contentId: string, listener: AttachListener): () => void {
    const existing = this.#collabManagerMap.get(contentId);

    if (existing) {
      listener(existing);
    }

    let listeners = this.#attachListeners.get(contentId);

    if (!listeners) {
      listeners = new Set();

      this.#attachListeners.set(contentId, listeners);
    }

    listeners.add(listener);

    return () => {
      const attachedListener = this.#attachListeners.get(contentId);

      if (!attachedListener) {
        return;
      }

      attachedListener.delete(listener);

      if (attachedListener.size === 0) {
        this.#attachListeners.delete(contentId);
      }
    };
  }

  // TODO not used right now, but we will eventually use it to properly remove
  // collab managers
  removeCollabManager(contentId: string): void {
    this.#collabManagerMap.delete(contentId);
    // Intentionally NOT clearing #attachListeners — bootstrap may dispose +
    // re-setup without calling removeCollabManager, and we still want to fire
    // on the next setCollabManager call.
  }

  closeInactiveSessions(currentContentId: string): void {
    for (const [contentId, manager] of this.#collabManagerMap.entries()) {
      if (contentId !== currentContentId) {
        manager.killSession();
      }
    }
  }
  listSessions(): string[] {
    return Array.from(this.#collabManagerMap.keys());
  }
}

export const collabStateManager = new CollabStateManager();
